import { useEffect, useState } from 'react';
import { Download, Mic, Sparkles, Check, Loader2 } from 'lucide-react';
import { Progress } from './ui/progress';

const stages = [
  { id: 'pending', label: 'Starting...', icon: Loader2, progress: 0 },
  { id: 'downloading', label: 'Downloading audio', icon: Download, progress: 25 },
  { id: 'transcribing', label: 'Transcribing sermon', icon: Mic, progress: 50 },
  { id: 'analyzing', label: 'Analyzing with AI', icon: Sparkles, progress: 75 },
  { id: 'completed', label: 'Complete!', icon: Check, progress: 100 },
];

export function AnalysisProgress({ jobStatus, onClose }) {
  const [currentStage, setCurrentStage] = useState(0);
  const [progressValue, setProgressValue] = useState(0);
  
  useEffect(() => {
    if (!jobStatus) return;
    
    const stageIndex = stages.findIndex(s => s.id === jobStatus.status);
    if (stageIndex >= 0) {
      setCurrentStage(stageIndex);
      
      // Animate progress bar
      const targetProgress = jobStatus.progress || stages[stageIndex].progress;
      const animateProgress = () => {
        setProgressValue(prev => {
          if (prev < targetProgress) {
            return Math.min(prev + 2, targetProgress);
          }
          return prev;
        });
      };
      
      const interval = setInterval(animateProgress, 50);
      return () => clearInterval(interval);
    }
  }, [jobStatus]);

  if (!jobStatus) return null;

  const isComplete = jobStatus.status === 'completed';
  const isFailed = jobStatus.status === 'failed';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="analysis-progress-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e3a8a] to-[#0ea5e9] p-6 text-white">
          <h3 className="text-xl font-semibold mb-1" data-testid="analysis-progress-title">
            {isFailed ? 'Analysis Failed' : isComplete ? 'Analysis Complete!' : 'Analyzing Sermon'}
          </h3>
          <p className="text-blue-100 text-sm">
            {isFailed 
              ? 'Something went wrong. Please try again.'
              : isComplete 
                ? 'Your sermon has been analyzed successfully!'
                : 'This may take a few minutes for longer sermons...'}
          </p>
        </div>

        {/* Progress Section */}
        <div className="p-6">
          {/* Progress Bar */}
          {!isFailed && (
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-stone-600">{jobStatus.message || 'Processing...'}</span>
                <span className="font-medium text-[#1e3a8a]">{progressValue}%</span>
              </div>
              <Progress value={progressValue} className="h-3" />
            </div>
          )}

          {/* Stage Indicators */}
          <div className="space-y-3">
            {stages.slice(1, -1).map((stage, index) => {
              const stageNum = index + 1;
              const isActive = currentStage === stageNum;
              const isCompleted = currentStage > stageNum || isComplete;
              const isPending = currentStage < stageNum;
              const Icon = stage.icon;

              return (
                <div 
                  key={stage.id}
                  className={`flex items-center gap-4 p-3 rounded-lg transition-all duration-300 ${
                    isActive 
                      ? 'bg-[#1e3a8a]/10 border border-[#1e3a8a]/20' 
                      : isCompleted 
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-stone-50 border border-stone-200'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isActive 
                      ? 'bg-[#1e3a8a] text-white' 
                      : isCompleted 
                        ? 'bg-green-500 text-white'
                        : 'bg-stone-200 text-stone-400'
                  }`}>
                    {isActive ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isCompleted ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${
                      isActive 
                        ? 'text-[#1e3a8a]' 
                        : isCompleted 
                          ? 'text-green-700'
                          : 'text-stone-400'
                    }`}>
                      {stage.label}
                    </p>
                    {isActive && (
                      <p className="text-sm text-stone-500 mt-0.5">
                        {stage.id === 'downloading' && 'Fetching audio from YouTube...'}
                        {stage.id === 'transcribing' && 'Converting speech to text...'}
                        {stage.id === 'analyzing' && 'Extracting insights with AI...'}
                      </p>
                    )}
                  </div>
                  {isCompleted && !isActive && (
                    <span className="text-xs text-green-600 font-medium">Done</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Error Message */}
          {isFailed && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{jobStatus.message || 'An error occurred'}</p>
            </div>
          )}

          {/* Close/Retry Button for failures */}
          {isFailed && (
            <button
              onClick={onClose}
              className="mt-4 w-full bg-stone-100 hover:bg-stone-200 text-stone-700 font-medium py-3 rounded-lg transition-colors"
              data-testid="analysis-progress-close-btn"
            >
              Close
            </button>
          )}
        </div>

        {/* Tip */}
        {!isFailed && !isComplete && (
          <div className="px-6 pb-6">
            <p className="text-xs text-stone-400 text-center" data-testid="analysis-progress-tip">
              💡 Tip: You can close this and check your library later - analysis continues in the background.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
